import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Camera, CheckCircle2, XCircle, AlertTriangle, Search, ScanLine, Users, CalendarDays } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../utils/api';
import { toast } from 'sonner';
import { useAuthStore } from '../store/authStore';
import { useNavigate } from 'react-router-dom';

const SCANNER_DOM_ID = 'qr-scanner-region';

// Short feedback beep — high double-tone for valid, low buzz for invalid
const playBeep = (valid) => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const tone = (freq, start, dur) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.25, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur);
    };
    if (valid) {
      tone(880, 0, 0.12);
      tone(1320, 0.13, 0.15);
    } else {
      tone(220, 0, 0.3);
    }
    setTimeout(() => ctx.close(), 800);
  } catch (e) { /* audio unavailable */ }
};

export default function AdminTicketScanner() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [manualId, setManualId] = useState('');
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraStarting, setCameraStarting] = useState(false);
  const [admittedCount, setAdmittedCount] = useState(0);
  const [events, setEvents] = useState([]);
  const [eventFilter, setEventFilter] = useState('');
  const eventFilterRef = useRef('');
  const html5QrRef = useRef(null);
  const lastScannedRef = useRef({ id: null, at: 0 });

  // Load upcoming events for the door-lock filter
  useEffect(() => {
    api.get('/events?limit=50').then((res) => setEvents(res.data || [])).catch(() => {});
  }, []);

  // Guard: admin/owner only
  useEffect(() => {
    if (user && !['admin', 'owner', 'overseer'].includes(user.role)) {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [user, navigate]);

  const validate = useCallback(async (payload) => {
    setError('');
    setScanning(true);
    try {
      const body = {};
      // qr payload from camera looks like RAZIENT|tkt_X|evt_Y|user_Z
      if (payload.includes('|')) body.qr_payload = payload;
      else body.ticket_id = payload.trim();
      if (eventFilterRef.current) body.event_id = eventFilterRef.current;

      const res = await api.post('/admin/tickets/scan', body);
      setResult(res.data);
      playBeep(!!res.data?.valid);
      if (navigator.vibrate) navigator.vibrate(res.data?.valid ? 80 : [60, 60, 60]);
      if (res.data?.valid) {
        setAdmittedCount((c) => c + 1);
        toast.success(`Valid ticket — ${res.data.ticket?.attendee_name || ''}`);
      } else if (res.data?.reason === 'wrong_event') {
        toast.error('Wrong event — ticket is for a different night');
      } else {
        toast.warning('Already scanned or invalid');
      }
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Scan failed';
      setResult(null);
      setError(detail);
      playBeep(false);
      if (navigator.vibrate) navigator.vibrate([60, 60, 60]);
      toast.error(detail);
      return false;
    } finally {
      setScanning(false);
    }
  }, []);

  // Start camera
  const startCamera = useCallback(async () => {
    setError('');
    setCameraStarting(true);
    try {
      const { Html5Qrcode } = await import('html5-qrcode');
      const region = document.getElementById(SCANNER_DOM_ID);
      if (!region) return;
      const scanner = new Html5Qrcode(SCANNER_DOM_ID, /* verbose */ false);
      html5QrRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decodedText) => {
          // Dedupe rapid repeats of same code
          const now = Date.now();
          if (
            lastScannedRef.current.id === decodedText &&
            now - lastScannedRef.current.at < 3000
          ) {
            return;
          }
          lastScannedRef.current = { id: decodedText, at: now };
          validate(decodedText);
        },
        () => {
          /* ignore frame parse errors */
        }
      );
      setCameraOn(true);
    } catch (e) {
      setError('Camera unavailable — allow camera access, or use manual entry below.');
      console.error(e);
    } finally {
      setCameraStarting(false);
    }
  }, [validate]);

  // Stop and clear the camera scanner if running. Stable — only touches the ref.
  const stopCamera = async () => {
    try {
      if (html5QrRef.current) {
        await html5QrRef.current.stop();
        await html5QrRef.current.clear();
        html5QrRef.current = null;
      }
    } catch (e) {
      // ignore
    }
    setCameraOn(false);
  };

  // Auto-start the camera on mount so door staff can scan immediately
  useEffect(() => {
    const t = setTimeout(() => {
      if (!html5QrRef.current) startCamera();
    }, 400);
    return () => clearTimeout(t);
  }, [startCamera]);

  // Cleanup on unmount — inline so we don't need to declare stopCamera as a dep.
  useEffect(() => {
    return () => {
      const scanner = html5QrRef.current;
      if (scanner) {
        scanner.stop().catch(() => {});
        scanner.clear?.();
        html5QrRef.current = null;
      }
    };
  }, []);

  const onManualSubmit = async (e) => {
    e?.preventDefault?.();
    if (!manualId.trim()) {
      toast.error('Enter a ticket ID');
      return;
    }
    const ok = await validate(manualId.trim());
    if (ok) setManualId('');
  };

  const ticket = result?.ticket;

  return (
    <div className="min-h-screen pt-24 pb-12 px-4 relative z-10">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <ScanLine className="w-7 h-7 text-purple-400" />
            <h1 className="text-3xl font-bold text-white">Ticket Scanner</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/events/stats')}
              data-testid="view-stats-btn"
            >
              Attendance stats
            </Button>
            <div
              className="flex items-center gap-2 bg-green-500/10 border border-green-500/30 rounded-full px-4 py-1.5 text-green-300 text-sm font-semibold"
              data-testid="admitted-count"
            >
              <Users className="w-4 h-4" />
              {admittedCount} admitted
            </div>
          </div>
        </div>
        <p className="text-gray-400 mb-4">
          Camera starts automatically — point it at a ticket QR. Or paste a ticket ID below.
        </p>

        {/* Event door-lock filter */}
        <div className="bg-white/5 rounded-2xl border border-white/10 p-4 mb-6">
          <div className="flex items-center gap-2 mb-2 text-white">
            <CalendarDays className="w-5 h-5 text-purple-400" />
            <span className="font-semibold">Scanning for</span>
          </div>
          <select
            value={eventFilter}
            onChange={(e) => {
              setEventFilter(e.target.value);
              eventFilterRef.current = e.target.value;
              setResult(null);
              setError('');
            }}
            className="w-full bg-black/50 border border-white/20 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-purple-500"
            data-testid="event-filter-select"
          >
            <option value="">All events (no lock)</option>
            {events.map((ev) => (
              <option key={ev.event_id} value={ev.event_id}>
                {ev.title} — {ev.event_date}
              </option>
            ))}
          </select>
          {eventFilter && (
            <p className="text-xs text-purple-300 mt-2" data-testid="event-lock-note">
              🔒 Door locked — tickets for any other event will be rejected (and not consumed).
            </p>
          )}
        </div>

        {/* BIG door-visible result banner */}
        {result && (
          <div
            className={`rounded-2xl px-6 py-5 mb-6 border-2 flex items-center gap-4 ${
              result.valid
                ? 'bg-green-500/20 border-green-400'
                : result.reason === 'wrong_event'
                ? 'bg-red-500/20 border-red-400'
                : 'bg-yellow-500/20 border-yellow-400'
            }`}
            data-testid="scan-banner"
          >
            {result.valid ? (
              <CheckCircle2 className="w-12 h-12 text-green-400 flex-shrink-0" />
            ) : (
              <XCircle className={`w-12 h-12 flex-shrink-0 ${result.reason === 'wrong_event' ? 'text-red-400' : 'text-yellow-400'}`} />
            )}
            <div className="min-w-0">
              <p className={`text-2xl sm:text-3xl font-extrabold ${
                result.valid ? 'text-green-300' : result.reason === 'wrong_event' ? 'text-red-300' : 'text-yellow-300'
              }`}>
                {result.valid ? 'ADMIT' : result.reason === 'wrong_event' ? 'WRONG EVENT' : 'ALREADY SCANNED'}
              </p>
              <p className="text-white text-lg font-semibold truncate">
                {ticket?.attendee_name || 'Unknown attendee'}
                {ticket?.event_title ? ` — ${ticket.event_title}` : ''}
              </p>
              {result.reason === 'wrong_event' && (
                <p className="text-xs text-red-200">
                  Ticket is for "{ticket?.event_title}" ({ticket?.event_date}) — not this event. Ticket NOT consumed.
                </p>
              )}
              {!result.valid && result.reason !== 'wrong_event' && result.scanned_at && (
                <p className="text-xs text-gray-300">
                  Originally scanned at {new Date(result.scanned_at).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        )}

        {error && (
          <div
            className="bg-red-500/15 border border-red-500/30 text-red-300 rounded-xl p-4 mb-6 flex items-center gap-2"
            data-testid="scan-error"
          >
            <AlertTriangle className="w-5 h-5" />
            {error}
          </div>
        )}

        {/* Camera */}
        <div className="bg-white/5 rounded-2xl border border-white/10 p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-white">
              <Camera className="w-5 h-5 text-purple-400" />
              <span className="font-semibold">Camera scanner</span>
              {cameraOn && (
                <span className="flex items-center gap-1.5 text-xs text-green-400 ml-2">
                  <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" /> live
                </span>
              )}
            </div>
            {!cameraOn ? (
              <Button onClick={startCamera} disabled={cameraStarting} data-testid="start-camera-btn">
                {cameraStarting ? 'Starting…' : 'Start camera'}
              </Button>
            ) : (
              <Button variant="outline" onClick={stopCamera} data-testid="stop-camera-btn">
                Stop camera
              </Button>
            )}
          </div>
          <div
            id={SCANNER_DOM_ID}
            className="w-full aspect-square max-w-sm mx-auto bg-black/40 rounded-xl overflow-hidden flex items-center justify-center text-gray-500"
            data-testid="scanner-region"
          >
            {!cameraOn && !cameraStarting && <span className="text-sm">Camera preview will appear here</span>}
          </div>
        </div>

        {/* Manual entry */}
        <form
          onSubmit={onManualSubmit}
          className="bg-white/5 rounded-2xl border border-white/10 p-5 mb-6"
        >
          <div className="flex items-center gap-2 mb-3 text-white">
            <Search className="w-5 h-5 text-purple-400" />
            <span className="font-semibold">Manual ticket lookup</span>
          </div>
          <div className="flex gap-3">
            <Input
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              placeholder="tkt_XXXXXXXXXXXXXX"
              data-testid="manual-ticket-input"
            />
            <Button type="submit" disabled={scanning} data-testid="manual-validate-btn">
              {scanning ? 'Checking…' : 'Validate'}
            </Button>
          </div>
        </form>

        {/* Ticket details */}
        {ticket && (
          <div
            className={`rounded-2xl p-6 border ${
              result.valid
                ? 'bg-green-500/10 border-green-500/40'
                : 'bg-yellow-500/10 border-yellow-500/40'
            }`}
            data-testid="scan-result"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-400">Attendee</p>
                <p className="text-lg text-white font-semibold">{ticket.attendee_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Event</p>
                <p className="text-lg text-white font-semibold">{ticket.event_title}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">When</p>
                <p className="text-white">
                  {ticket.event_date} {ticket.event_time}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Where</p>
                <p className="text-white">{ticket.event_location}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-sm text-gray-400">Ticket ID</p>
                <p className="font-mono text-purple-300 text-sm break-all">{ticket.ticket_id}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
